import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useLang, STR } from "@/i18n/translations";

export default function About() {
  const { t, lang } = useLang();
  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <p className="eyebrow mb-5">{t(STR.about.eyebrow)}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl">{t(STR.about.title)}</h1>
        <div className="gold-line w-24 my-10" />

        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <p className="text-cream/70 text-lg leading-relaxed">{t(STR.about.visionText)}</p>
          <div className="card-dark rounded-2xl aspect-square flex items-center justify-center grain relative overflow-hidden">
            <ElephantLogo className="w-64 h-64 animate-glow" />
          </div>
        </div>

        <h2 className="font-serif text-3xl lg:text-4xl mt-24 mb-10">{t({ fr: "Nos valeurs", en: "Our values" })}</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {STR.about.values.map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="card-dark rounded-2xl p-8"
              data-testid={`about-value-${i}`}
            >
              <span className="font-serif text-4xl text-gold/40">0{i + 1}</span>
              <h3 className="font-serif text-2xl mt-4 mb-2">{lang === "fr" ? v.fr : v.en}</h3>
              <p className="text-cream/60 text-sm">{lang === "fr" ? v.dfr : v.den}</p>
            </motion.div>
          ))}
        </div>

        <h2 className="font-serif text-3xl lg:text-4xl mt-24 mb-10">{t({ fr: "Nos références", en: "Our references" })}</h2>
        <div className="card-dark rounded-2xl p-8 lg:p-12 border-l-2 border-gold grid lg:grid-cols-[auto_1fr] gap-8 items-center" data-testid="about-reference-ans-t-crazy">
          <div className="text-center lg:pr-8 lg:border-r lg:border-gold/20">
            <p className="font-serif text-5xl text-gold">11</p>
            <p className="eyebrow mt-2">{t({ fr: "Septembre 2026", en: "September 2026" })}</p>
          </div>
          <div>
            <p className="eyebrow mb-3">{t({ fr: "Communication de concert", en: "Concert communication" })}</p>
            <h3 className="font-serif text-2xl lg:text-3xl mb-3">Ans-T Crazy — Zénith Paris-La Villette</h3>
            <p className="text-cream/70 leading-relaxed max-w-3xl">{t({
              fr: "Sily Agency a travaillé sur la communication du concert de l'artiste guinéen Ans-T Crazy, figure majeure des musiques urbaines africaines, au Zénith Paris-La Villette (6 804 places) le vendredi 11 septembre 2026. Un concert produit par FY One Way Prod.",
              en: "Sily Agency worked on the communication for Guinean artist Ans-T Crazy's concert, a major figure of African urban music, at the Zénith Paris-La Villette (6,804 capacity) on Friday, September 11, 2026. A concert produced by FY One Way Prod.",
            })}</p>
          </div>
        </div>

        <div className="mt-24 card-dark rounded-2xl p-10 lg:p-14 grid lg:grid-cols-[1fr_auto] gap-8 items-center grain relative overflow-hidden">
          <div>
            <h2 className="font-serif text-3xl lg:text-4xl mb-4">{t({ fr: "L'éléphant, notre emblème", en: "The elephant, our emblem" })}</h2>
            <p className="text-cream/70 leading-relaxed max-w-2xl">{t({
              fr: "La tête d'éléphant incarne la force, la sagesse et la majesté — les valeurs qui guident chaque projet que nous portons.",
              en: "The elephant head embodies strength, wisdom and majesty — the values guiding every project we carry.",
            })}</p>
            <Link to="/contact" data-testid="about-cta-btn" className="btn-gold rounded-full px-8 py-4 mt-8 inline-block">{t(STR.nav.contact)}</Link>
          </div>
          <ElephantLogo className="w-40 h-40 shrink-0" />
        </div>
      </div>
    </div>
  );
}
